import React from "react";
import { coreapi } from "./coreapi";

/** Audit action: the signed-in app was really on screen. */
export const AUTHENTICATED_VIEW_ACTION = "app.authenticated_view";

/**
 * A tab left open counts again after this long — often enough to show the
 * app being used across a day, rare enough to stay far from one event per
 * glance.
 */
const REPORT_INTERVAL_MS = 30 * 60 * 1000;

// Module scope, not a ref: it has to survive the layout remounting (a trip
// through /subscribe and back) and React StrictMode's double-run effects.
let lastReportedAt = 0;

function reportIfDue() {
  if (document.visibilityState !== "visible") return;
  const now = Date.now();
  if (now - lastReportedAt < REPORT_INTERVAL_MS) return;
  lastReportedAt = now;
  coreapi.audit(AUTHENTICATED_VIEW_ACTION);
}

/**
 * Tell the audit trail that the user is looking at the authenticated app.
 *
 * This — not "a session was created" — is the evidence that someone used the
 * product: a session can exist without a person behind it (a mail scanner
 * following a magic link, a login that never gets past a gate). Shared by
 * both apps: mount it in the layout that renders only once RouteGuard has let
 * the user through to the authenticated app.
 *
 * Reports when the layout is on screen in a visible tab: on mount, and again
 * when the tab comes back to the foreground, at most once per
 * `REPORT_INTERVAL_MS`. A page loaded in a background tab is not "seen" until
 * it is brought forward.
 */
export function useAuditAuthenticatedView(): void {
  React.useEffect(() => {
    reportIfDue();
    document.addEventListener("visibilitychange", reportIfDue);
    return () => {
      document.removeEventListener("visibilitychange", reportIfDue);
    };
  }, []);
}
